import { TFunction } from "i18next";
import _ from "lodash";

import {
  hasKeys,
  IReportApiDto,
  IReportCriteriaFiltersSchemaValues,
  ReportPeriodGroupingEnum,
} from "@ryde/shared";

import {
  buildReportChartDimensionOptions,
  getReportGroupingDimensionColumns,
  isMetricColumn,
  mapColumnToAxisOption,
  shouldSyncReportGroupingForChartDimension,
} from "../../../../reports/drawers/report-chart-drawer/parts/report-chart.utils.data";
import {
  DashboardChartType,
  TDashboardCardSize,
  TDashboardCardSource,
} from "../../dynamic-dashboard.models";
import { IReportChartOptions } from "./dashboard-card-editor.models";

const EMPTY_OPTIONS: IReportChartOptions = {
  dimensionOptions: [],
  metricOptions: [],
  flexGroupingTemplateKeys: [],
};

export interface ICardDraft {
  cardSource: TDashboardCardSource;
  systemCardName: string;
  reportId: number | "";
  name: string;
  chartType: DashboardChartType;
  size: TDashboardCardSize;
  dimensionKey: string;
  metricKey: string;
  seriesKey: string;
  cardFilters: IReportCriteriaFiltersSchemaValues;
}

/**
 * Everything the card editor can change, as one comparable string. Compared
 * against the value taken when the panel opened, this is what tells the modal
 * there are unsaved edits worth warning about. A signature beats a dirty flag per
 * setter: a newly added field is covered the moment it joins the draft.
 *
 * Filter keys are sorted so merely removing and re-adding a filter — which
 * reorders the object — doesn't read as a change.
 *
 * `name` counts only for a report card. A ready-made card has no name field and
 * saves no title (its header renders `dashboard.<CARD>.TITLE`), so including the
 * name there would warn about unsaved changes that saving could never keep.
 */
export const buildCardDraftSignature = (draft: ICardDraft): string =>
  JSON.stringify({
    cardSource: draft.cardSource,
    systemCardName: draft.systemCardName,
    reportId: draft.reportId,
    name: draft.cardSource === "system" ? "" : draft.name.trim(),
    chartType: draft.chartType,
    size: draft.size,
    dimensionKey: draft.dimensionKey,
    metricKey: draft.metricKey,
    seriesKey: draft.seriesKey,
    cardFilters: Object.keys(draft.cardFilters)
      .sort()
      .map(key => [key, draft.cardFilters[key]]),
  });

/**
 * What the X on a filter does, matching the grids
 * (`useTableFiltersActions.handleRemoveFilter`): on a filter that still HAS values
 * it only clears them, so another value can be picked without re-adding the
 * filter; on an already-empty filter it removes the filter itself. `null` is the
 * shared marker for "filter present, no value chosen".
 */
export const removeOrClearCardFilter = ({
  filters,
  templateKey,
}: {
  filters: IReportCriteriaFiltersSchemaValues;
  templateKey: string;
}): IReportCriteriaFiltersSchemaValues =>
  _.isEmpty(filters[templateKey])
    ? _.omit(filters, templateKey)
    : { ...filters, [templateKey]: null };

/**
 * Derive the chart's Group-by + Metric options from a fetched report, reusing
 * the reports engine's own option builders — so the dashboard offers exactly
 * the dimensions/metrics the Reports "open chart" does (and only the columns
 * the user is permitted to see, since the server prunes the rest).
 */
export const getReportChartOptions = (
  dto: IReportApiDto | undefined,
  t: TFunction,
): IReportChartOptions => {
  if (!dto) {
    return EMPTY_OPTIONS;
  }
  const columns = dto.layout?.columns ?? [];
  const template = dto.reportTemplate;
  const flexGroupingTemplateKeys =
    template?.flexGroupingColumns && hasKeys(template.flexGroupingColumns)
      ? Object.keys(template.flexGroupingColumns)
      : [];
  const hasFlexGroupingTemplate = flexGroupingTemplateKeys.length > 0;
  const groupingDimensionColumns = getReportGroupingDimensionColumns({
    columns,
    groupingParamKeys: [],
    isFlexPeriodApplied: false,
    hasFlexGroupingTemplate,
  });
  const dimensionOptions = buildReportChartDimensionOptions({
    columns,
    groupingParamKeys: [],
    flexGroupingTemplateKeys,
    flexGroupingColumns: template?.flexGroupingColumns,
    periodGroupingColumn: template?.periodGroupingColumn,
    hasReportGroupingDimension: groupingDimensionColumns.length > 0,
    t,
  });
  const metricOptions = columns
    .filter(isMetricColumn)
    .map(col => mapColumnToAxisOption(col, t));
  return { dimensionOptions, metricOptions, flexGroupingTemplateKeys };
};

/** Translate a chosen dimension into the flex grouping the fetch must send. */
const deriveGrouping = (
  dimensionKey: string,
  flexGroupingTemplateKeys: string[],
): { groupingParams: string[]; periodGrouping?: ReportPeriodGroupingEnum } => {
  const sync = shouldSyncReportGroupingForChartDimension({
    dimensionKey,
    groupingParamKeys: [],
    flexGroupingTemplateKeys,
    isFlexPeriodApplied: false,
  });
  if (sync?.appendFlexGroupingKey) {
    return { groupingParams: [sync.appendFlexGroupingKey] };
  }
  if (sync?.enablePeriodGrouping) {
    return {
      groupingParams: [],
      periodGrouping: ReportPeriodGroupingEnum.Monthly,
    };
  }
  return { groupingParams: [] };
};

/** Merge grouping across dimensions (clustered/stacked group by category + series). */
export const deriveGroupingForDims = (
  dimensionKeys: string[],
  flexGroupingTemplateKeys: string[],
): { groupingParams: string[]; periodGrouping?: ReportPeriodGroupingEnum } =>
  dimensionKeys.reduce<{
    groupingParams: string[];
    periodGrouping?: ReportPeriodGroupingEnum;
  }>(
    (acc, key) => {
      const next = deriveGrouping(key, flexGroupingTemplateKeys);
      return {
        groupingParams: Array.from(
          new Set([...acc.groupingParams, ...next.groupingParams]),
        ),
        periodGrouping: acc.periodGrouping ?? next.periodGrouping,
      };
    },
    { groupingParams: [], periodGrouping: undefined },
  );
